import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { AlertCircle, Eye, EyeOff, RefreshCw, Copy, Check } from 'lucide-react'

// Role display metadata keyed by role name
const ROLE_META: Record<string, { subtitle: string; isPrimary?: boolean; warning?: string }> = {
  CCO:       { subtitle: 'Institution Admin, full access, primary administrator.',    isPrimary: true, warning: 'Only one Institution Admin is permitted per institution.' },
  President: { subtitle: 'Head of the association, full access, primary leader.',                 isPrimary: true, warning: 'Only one President is permitted per association.' },
  Owner:     { subtitle: 'Account owner, full access.',                                           isPrimary: true, warning: 'Only one Owner is permitted per account.' },
  Admin:     { subtitle: 'General administrator, broad access to aid the Institution Admin.' },
  Secretary: { subtitle: 'Administrative support, can manage members and edit association details.' },
  Editor:    { subtitle: 'Lead journalist / editor, full newsroom access.', isPrimary: true, warning: 'Only one Editor is permitted per institution.' },
}

function generatePassword(length = 14): string {
  const lower   = 'abcdefghijkmnpqrstuvwxyz'
  const upper   = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const digits  = '23456789'
  const symbols = '!@#$%&*'
  const all     = lower + upper + digits + symbols
  const bytes   = crypto.getRandomValues(new Uint8Array(length + 4))
  const pwd = [
    lower  [bytes[0] % lower.length],
    upper  [bytes[1] % upper.length],
    digits [bytes[2] % digits.length],
    symbols[bytes[3] % symbols.length],
    ...Array.from(bytes.slice(4), b => all[b % all.length]),
  ]
  for (let i = pwd.length - 1; i > 0; i--) {
    const j = crypto.getRandomValues(new Uint8Array(1))[0] % (i + 1)
    ;[pwd[i], pwd[j]] = [pwd[j], pwd[i]]
  }
  return pwd.join('')
}

const schema = z.object({
  last_name:               z.string().min(1, 'Last name required'),
  first_name:              z.string().min(1, 'First name required'),
  email:                   z.string().email('Valid email required'),
  password:                z.string().min(8, 'Minimum 8 characters'),
  role_id:                 z.string().min(1, 'Role required'),
  require_password_change: z.boolean().default(true),
})

type FormData = z.infer<typeof schema>

interface CreateUserSheetProps {
  org_id:       string
  org_name:     string
  open:         boolean
  onClose:      () => void
  onSuccess?:   (data: any) => void
  defaultRole?: string
}

export function CreateUserSheet({
  org_id, org_name, open, onClose, onSuccess, defaultRole
}: CreateUserSheetProps) {
  const queryClient = useQueryClient()
  const [showPassword, setShowPassword] = useState(false)
  const [copied, setCopied]             = useState(false)

  // Query ALL system roles for the org (no name filter) so associations get President/Secretary
  const { data: roles } = useQuery({
    queryKey: ['org-roles-onboarding', org_id],
    enabled:  !!org_id && open,
    queryFn:  async () => {
      const { data } = await supabase
        .from('roles')
        .select('id, name, display_name, description')
        .eq('org_id', org_id)
        .eq('is_system', true)
        .order('name')
      return (data || []) as { id: string; name: string; display_name: string | null; description: string | null }[]
    },
  })

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { require_password_change: true },
  })

  const { setValue } = form

  useEffect(() => {
    if (defaultRole && roles && open) {
      const target = roles.find(r => r.name === defaultRole)
      if (target) setValue('role_id', target.id)
    }
  }, [defaultRole, roles, open, setValue])

  function handleGenerate() {
    const pwd = generatePassword()
    setValue('password', pwd, { shouldValidate: true })
    setValue('require_password_change', true)
    setShowPassword(true)
  }

  async function handleCopy() {
    const pwd = form.getValues('password')
    if (!pwd) return
    let success = false
    try {
      await navigator.clipboard.writeText(pwd)
      success = true
    } catch {
      // Fallback for non-HTTPS or restricted contexts
      try {
        const el = document.createElement('textarea')
        el.value = pwd
        el.setAttribute('readonly', '')
        el.style.cssText = 'position:fixed;left:-9999px;top:0;width:2em;height:2em;border:none;outline:none;background:transparent'
        document.body.appendChild(el)
        el.focus()
        el.select()
        el.setSelectionRange(0, 99999)
        success = document.execCommand('copy')
        document.body.removeChild(el)
      } catch {
        // Last resort: prompt dialog for manual copy
        window.prompt('Copy this password (Ctrl+C / Cmd+C):', pwd)
        success = true
      }
    }
    if (success) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    }
  }

  const lastName        = form.watch('last_name')  || ''
  const firstName       = form.watch('first_name') || ''
  const fullNamePreview = `${lastName} ${firstName}`.trim()
  const selectedRoleId  = form.watch('role_id')

  const mutation = useMutation({
    mutationFn: async (data: FormData) => {
      const { data: { session } } = await supabase.auth.getSession()
      const full_name = `${data.last_name} ${data.first_name}`.trim()

      const result = await supabase.functions.invoke('create-org-user', {
        body: {
          email:                   data.email,
          password:                data.password,
          full_name,
          org_id,
          role_id:                 data.role_id,
          require_password_change: data.require_password_change,
        },
        headers: { Authorization: `Bearer ${session?.access_token}` },
      })

      if (result.error) throw new Error(result.error.message)
      const body = result.data as any
      if (body?.error) throw new Error(body.error)
      return body
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-institution-orgs'] })
      queryClient.invalidateQueries({ queryKey: ['admin-all-institution-members'] })
      queryClient.invalidateQueries({ queryKey: ['all-users'] })
      queryClient.invalidateQueries({ queryKey: ['associations'] })
      onSuccess?.(data)
      form.reset()
    },
  })

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[500px] overflow-y-auto">
        <SheetHeader className="pb-4 border-b border-[#DDD8CE]">
          <SheetTitle className="text-[#0D2240]">Add User to {org_name}</SheetTitle>
          <SheetDescription className="text-justify text-[12px]">
            Create a new user account and assign it to this organisation.
            The email is auto-confirmed and the user will be prompted to
            set a new password on first login.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={form.handleSubmit(d => mutation.mutate(d))} className="space-y-4 pt-4 px-1">

          {/* Name fields */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-[11.5px] font-medium text-muted-foreground uppercase tracking-wide">Last Name</label>
              <input {...form.register('last_name')} placeholder="e.g. Kofi"
                className="w-full px-3 py-2 border border-[#DDD8CE] rounded-lg text-[13px] focus:outline-none focus:border-[#006B3C]" />
              {form.formState.errors.last_name && <p className="text-justify text-red-500 text-[11px]">{form.formState.errors.last_name.message}</p>}
            </div>
            <div className="space-y-1">
              <label className="text-[11.5px] font-medium text-muted-foreground uppercase tracking-wide">First Name</label>
              <input {...form.register('first_name')} placeholder="e.g. Amara"
                className="w-full px-3 py-2 border border-[#DDD8CE] rounded-lg text-[13px] focus:outline-none focus:border-[#006B3C]" />
              {form.formState.errors.first_name && <p className="text-justify text-red-500 text-[11px]">{form.formState.errors.first_name.message}</p>}
            </div>
          </div>

          {/* Name preview */}
          {fullNamePreview && (
            <div className="bg-[#F7F5F0] border border-[#DDD8CE] rounded-lg px-3 py-2 flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground">Display name:</span>
              <span className="text-[13px] font-medium text-[#0D2240]">{fullNamePreview}</span>
            </div>
          )}

          {/* Email */}
          <div className="space-y-1">
            <label className="text-[11.5px] font-medium text-muted-foreground uppercase tracking-wide">Email Address</label>
            <input {...form.register('email')} type="email" placeholder="user@organisation.com"
              className="w-full px-3 py-2 border border-[#DDD8CE] rounded-lg text-[13px] focus:outline-none focus:border-[#006B3C]" />
            {form.formState.errors.email && <p className="text-justify text-red-500 text-[11px]">{form.formState.errors.email.message}</p>}
          </div>

          {/* Password */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-[11.5px] font-medium text-muted-foreground uppercase tracking-wide">Password</label>
              <button type="button" onClick={handleGenerate}
                className="flex items-center gap-1 text-[11px] text-[#006B3C] hover:text-[#005A32] font-medium">
                <RefreshCw size={11} /> Generate
              </button>
            </div>
            <div className="relative">
              <input
                {...form.register('password')}
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter or generate a password…"
                className="w-full px-3 py-2 pr-20 border border-[#DDD8CE] rounded-lg text-[13px] focus:outline-none focus:border-[#006B3C] font-mono"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                {form.watch('password') && (
                  <button type="button" onClick={handleCopy}
                    className="p-1 text-muted-foreground hover:text-[#0D2240] transition-colors" title="Copy password">
                    {copied ? <Check size={13} className="text-[#006B3C]" /> : <Copy size={13} />}
                  </button>
                )}
                <button type="button" onClick={() => setShowPassword(p => !p)}
                  className="p-1 text-muted-foreground hover:text-[#0D2240] transition-colors">
                  {showPassword ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
            </div>
            {copied && <p className="text-justify text-[11px] text-[#006B3C]">✓ Password copied to clipboard</p>}
            {form.formState.errors.password && <p className="text-justify text-red-500 text-[11px]">{form.formState.errors.password.message}</p>}
          </div>

          {/* Role, dynamic, shows all system roles for the org */}
          <div className="space-y-1.5">
            <label className="text-[11.5px] font-medium text-muted-foreground uppercase tracking-wide">Role in Organisation</label>

            {!roles || roles.length === 0 ? (
              <p className="text-justify text-[11.5px] text-amber-600">
                No roles found for this organisation. Ensure the organisation was fully set up by a platform admin.
              </p>
            ) : (
              <div className="space-y-2">
                {roles.map(role => {
                  const meta = ROLE_META[role.name] ?? { subtitle: role.description ?? '' }
                  const label = role.display_name ?? role.name
                  return (
                    <label
                      key={role.id}
                      className={`flex items-start gap-3 p-3 border rounded-xl cursor-pointer transition-colors ${
                        selectedRoleId === role.id
                          ? 'border-[#006B3C] bg-[#E8F5EE]'
                          : 'border-[#DDD8CE] hover:border-[#006B3C]/40'
                      }`}
                    >
                      <input type="radio" {...form.register('role_id')} value={role.id} className="mt-1 accent-[#006B3C]" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[13px] font-semibold text-[#0D2240]">{label}</span>
                          {role.display_name && role.display_name !== role.name && (
                            <span className="text-[10px] text-muted-foreground font-mono">({role.name})</span>
                          )}
                        </div>
                        <div className="text-[11.5px] text-muted-foreground mt-0.5">{meta.subtitle}</div>
                        {meta.isPrimary && meta.warning && (
                          <div className="mt-2 flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                            <AlertCircle size={11} className="text-amber-600 shrink-0 mt-0.5" />
                            <p className="text-justify text-[11px] text-amber-700">{meta.warning}</p>
                          </div>
                        )}
                      </div>
                    </label>
                  )
                })}
              </div>
            )}
            {form.formState.errors.role_id && <p className="text-justify text-red-500 text-[11px]">{form.formState.errors.role_id.message}</p>}
          </div>

          {/* Require password change */}
          <div className="flex items-center justify-between py-3 px-3 bg-[#F7F5F0] rounded-lg border border-[#DDD8CE]">
            <div>
              <div className="text-[12.5px] font-medium text-[#0D2240]">Require password change on first login</div>
              <div className="text-[11px] text-muted-foreground">Always on when a password is admin-generated</div>
            </div>
            <input type="checkbox" {...form.register('require_password_change')} className="w-4 h-4 accent-[#006B3C] cursor-pointer" />
          </div>

          {/* Error */}
          {mutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2">
              <AlertCircle size={14} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-[12px] text-red-700 text-justify">{(mutation.error as Error).message}</p>
            </div>
          )}

          {/* Success */}
          {mutation.isSuccess && (
            <div className="bg-[#E1F5EE] border border-[#006B3C]/30 rounded-lg p-3 text-[12px] text-[#006B3C]">
              ✓ <strong>{fullNamePreview}</strong> has been created and added to {org_name}.
              They can log in at <span className="font-mono">/login</span>.
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-2 pt-2 border-t border-[#DDD8CE]">
            <button type="button" onClick={() => { onClose(); form.reset() }}
              className="flex-1 px-4 py-2.5 border border-[#DDD8CE] rounded-lg text-[13px] text-muted-foreground hover:bg-[#F7F5F0] transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={mutation.isPending}
              className="flex-1 px-4 py-2.5 bg-[#006B3C] text-white rounded-lg text-[13px] font-medium hover:bg-[#005A32] disabled:opacity-50 transition-colors">
              {mutation.isPending ? 'Creating…' : 'Create User'}
            </button>
          </div>

          {mutation.isSuccess && (
            <button type="button" onClick={() => { mutation.reset(); form.reset() }}
              className="w-full text-center text-[12px] text-[#006B3C] hover:underline">
              + Add another user to {org_name}
            </button>
          )}
        </form>
      </SheetContent>
    </Sheet>
  )
}
